import React from "react";
import { fetchMe, getCachedMe, subscribeMeCacheInvalidation, type MeResponse } from "../lib/meClient";
import { billingNoticeText, type BillingNoticeState } from "../lib/billingGraceCopy";
import { UI_Z_CLASS } from "../utils/uiZIndex";

const BANNER_ATTR = "data-billing-grace-banner";
const BANNER_HEIGHT_VAR = "--billing-grace-banner-height";
const BANNER_FALLBACK_HEIGHT = "2.75rem";

function clearBannerLayout(): void {
  const root = document.documentElement;
  root.removeAttribute(BANNER_ATTR);
  root.style.removeProperty(BANNER_HEIGHT_VAR);
}

function applyBannerHeight(px: number | string): void {
  const root = document.documentElement;
  root.setAttribute(BANNER_ATTR, "");
  root.style.setProperty(BANNER_HEIGHT_VAR, typeof px === "number" ? `${px}px` : px);
}

function observeBannerHeight(el: HTMLElement): () => void {
  const applyHeight = () => applyBannerHeight(el.getBoundingClientRect().height);
  applyHeight();
  const observer = new ResizeObserver(applyHeight);
  observer.observe(el);
  return () => observer.disconnect();
}

const CONTACT = "hallo@meisterpfad.de";

function isNoticeState(state: string | undefined): state is BillingNoticeState {
  return state === "payment_overdue" || state === "payment_overdue_urgent" || state === "ending" || state === "ended";
}

function noticeFor(me: MeResponse | null): { text: string; urgent: boolean } | null {
  if (me?.role !== "owner" && me?.role !== "admin") return null;
  const state = me.billing?.state;
  if (!isNoticeState(state)) return null;
  return {
    text: billingNoticeText(state, me.billing?.until, me.timezone || "Europe/Berlin"),
    urgent: state === "payment_overdue_urgent",
  };
}

function NoticeText({ text }: { text: string }) {
  const at = text.indexOf(CONTACT);
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <a className="font-medium underline" href={`mailto:${CONTACT}`}>
        {CONTACT}
      </a>
      {text.slice(at + CONTACT.length)}
    </>
  );
}

/** Sticky notice for instance owners and admins. It cannot be dismissed; billing never blocks access. */
export function BillingGraceBanner() {
  const [notice, setNotice] = React.useState<{ text: string; urgent: boolean } | null>(() => noticeFor(getCachedMe()));
  const stopObservingRef = React.useRef<(() => void) | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    const apply = (me: MeResponse | null) => {
      if (!cancelled) setNotice(noticeFor(me));
    };
    void fetchMe().then(apply);
    const unsubscribe = subscribeMeCacheInvalidation(() => {
      void fetchMe().then(apply);
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  React.useEffect(() => {
    return () => {
      stopObservingRef.current?.();
      stopObservingRef.current = null;
      clearBannerLayout();
    };
  }, []);

  const setBannerNode = React.useCallback((node: HTMLDivElement | null) => {
    stopObservingRef.current?.();
    stopObservingRef.current = null;
    if (!node) {
      clearBannerLayout();
      return;
    }
    applyBannerHeight(BANNER_FALLBACK_HEIGHT);
    stopObservingRef.current = observeBannerHeight(node);
  }, []);

  if (!notice) return null;

  return (
    <div
      ref={setBannerNode}
      className={`sticky top-[var(--app-update-banner-height,0px)] ${UI_Z_CLASS.appUpdateBanner} flex shrink-0 items-center justify-center px-4 py-2 text-center text-sm shadow-md ${
        notice.urgent
          ? "bg-red-50 text-red-950 dark:bg-red-950/50 dark:text-red-100"
          : "bg-amber-50 text-amber-950 dark:bg-amber-950/50 dark:text-amber-100"
      }`}
      role={notice.urgent ? "alert" : "status"}
      aria-live={notice.urgent ? "assertive" : "polite"}
    >
      <p className="min-w-0">
        <NoticeText text={notice.text} />
      </p>
    </div>
  );
}
